"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Play, Ellipsis as MoreHorizontal, CircleCheck as CheckCircle2, Circle, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ManageMyMachine() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [revealItalic, setRevealItalic] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const frameRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const t = window.setTimeout(() => setRevealItalic(true), 200);
    return () => window.clearTimeout(t);
  }, []);
  useEffect(() => {
    const onScroll = () => {
      if (!frameRef.current) return;
      const rect = frameRef.current.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, 1 - rect.top / window.innerHeight));
      setParallaxY(-16 * progress);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const columns = [{
    title: "Pending",
    count: "08",
    tasks: [{
      id: "MM-241",
      title: "Analyze vibration data",
      tag: "Sensors",
      state: "idle"
    }, {
      id: "MM-238",
      title: "Calibrate spindle speed",
      tag: "Hardware",
      state: "idle"
    }, {
      id: "MM-235",
      title: "Update firmware patch",
      tag: "System",
      state: "idle"
    }]
  }, {
    title: "Monitoring",
    count: "05",
    tasks: [{
      id: "MM-219",
      title: "Real-time thermal sync",
      tag: "Thermal",
      state: "active"
    }, {
      id: "MM-217",
      title: "Predictive maintenance",
      tag: "AI-Core",
      state: "active"
    }, {
      id: "MM-214",
      title: "Production throughput",
      tag: "Metrics",
      state: "active"
    }]
  }, {
    title: "Optimized",
    count: "12",
    tasks: [{
      id: "MM-208",
      title: "Energy efficiency logs",
      tag: "Power",
      state: "done"
    }, {
      id: "MM-204",
      title: "Custom cycle templates",
      tag: "Control",
      state: "done"
    }, {
      id: "MM-201",
      title: "Security audit export",
      tag: "Safety",
      state: "done"
    }]
  }];
  return <section data-section-id="18740" id="siemens-manage-mymachine-overview" className="bg-background text-foreground font-default" style={{
    borderTop: "2px solid var(--border)",
    borderBottom: "2px solid var(--border)"
  }}>
      <div className="min-h-[92vh] grid grid-cols-1 lg:grid-cols-12">
        {}
        <div ref={leftRef} className="lg:col-span-7 px-6 sm:px-10 lg:px-20 py-20 lg:py-0 flex flex-col justify-center lg:border-r-2 border-b-2 lg:border-b-0" style={{
        borderColor: "var(--border)",
        opacity: leftVisible ? 1 : 0,
        transform: leftVisible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 320ms cubic-bezier(0.2,0.8,0.2,1), transform 320ms cubic-bezier(0.2,0.8,0.2,1)"
      }}>
          <div className="font-mono uppercase text-xs tracking-[0.18em] text-muted-foreground mb-8" style={{
          transition: "opacity 320ms ease 60ms"
        }}>
            Industrial connectivity, made intelligent
          </div>

          <h1 className="font-default font-black text-foreground" style={{
          fontSize: "clamp(3.25rem, 7.5vw, 7.5rem)",
          letterSpacing: "-0.04em",
          lineHeight: 0.92
        }}>
            <span className="block">Control the assets</span>
            <span className="block">
              that drive the{" "}
              <span className="italic" style={{
              color: "var(--primary)",
              display: "inline-block",
              clipPath: revealItalic ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
              transition: "clip-path 420ms cubic-bezier(0.7, 0, 0.2, 1)"
            }}>
                factory
              </span>
            </span>
          </h1>

          <p className="mt-10 max-w-xl text-muted-foreground font-default" style={{
          fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
          lineHeight: 1.5,
          letterSpacing: "-0.005em"
        }}>
            Manage MyMachine is the digital command center for industrial operations. Real-time machine monitoring, predictive analytics, and seamless connectivity for your shop floor.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-5">
            <Link to="/SolutionsPage" className="inline-block">
              <Button className="font-default font-bold uppercase tracking-wide px-7 py-7 h-auto text-sm rounded-md" style={{
              backgroundColor: "var(--primary)",
              color: "var(--primary-foreground)",
              border: "2px solid var(--border)",
              boxShadow: "4px 4px 0 0 var(--border)"
            }}>
                Explore solutions
                <ArrowRight className="ml-1 size-4" />
              </Button>
            </Link>

            <Link to="https://www.youtube.com/watch?v=uBc6uBWGvaE" newTab className="inline-block">
              <Button variant="outline" className="font-default font-bold uppercase tracking-wide px-7 py-7 h-auto text-sm bg-transparent rounded-md" style={{
              color: "var(--foreground)",
              border: "2px solid var(--foreground)",
              boxShadow: "4px 4px 0 0 var(--foreground)"
            }}>
                <Play className="mr-1 size-4" />
                Watch system demo
              </Button>
            </Link>
          </div>

          <div className="mt-14 pt-8 grid grid-cols-3 gap-6 max-w-xl" style={{
          borderTop: "2px solid var(--border)"
        }}>
            <div data-index={0}>
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">Machines</div>
              <div className="font-default font-black text-2xl md:text-3xl mt-2 text-foreground">5,000+</div>
            </div>
            <div data-index={1}>
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">Accuracy</div>
              <div className="font-default font-black text-2xl md:text-3xl mt-2 text-foreground">99.99%</div>
            </div>
            <div data-index={2}>
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">Protocols</div>
              <div className="font-default font-black text-2xl md:text-3xl mt-2 text-foreground">60+</div>
            </div>
          </div>
        </div>

        {}
        <div ref={rightRef} className="lg:col-span-5 px-6 sm:px-10 lg:px-12 py-16 lg:py-0 flex items-start justify-center lg:pt-24" style={{
        opacity: rightVisible ? 1 : 0,
        transform: rightVisible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 320ms cubic-bezier(0.2,0.8,0.2,1) 120ms, transform 320ms cubic-bezier(0.2,0.8,0.2,1) 120ms"
      }}>
          <div ref={frameRef} className="group w-full max-w-[560px] relative" style={{
          border: "2px solid var(--foreground)",
          backgroundColor: "var(--card)",
          boxShadow: "8px 8px 0 0 var(--primary)",
          transition: "transform 180ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 180ms cubic-bezier(0.2,0.8,0.2,1)"
        }} onMouseEnter={e => {
          e.currentTarget.style.boxShadow = "4px 4px 0 0 var(--primary)";
          e.currentTarget.style.transform = "translate(4px, 4px)";
        }} onMouseLeave={e => {
          e.currentTarget.style.boxShadow = "8px 8px 0 0 var(--primary)";
          e.currentTarget.style.transform = "translate(0, 0)";
        }}>
            <div style={{
            transform: `translateY(${parallaxY}px)`,
            transition: "transform 80ms linear"
          }}>
              {}
              <div className="flex items-center justify-between px-4 py-3" style={{
              borderBottom: "2px solid var(--foreground)"
            }}>
                <div className="flex items-center gap-2">
                  <div className="size-2.5 rounded-md" style={{
                  backgroundColor: "var(--foreground)"
                }} />
                  <div className="size-2.5 rounded-md" style={{
                  backgroundColor: "var(--foreground)",
                  opacity: 0.5
                }} />
                  <div className="size-2.5 rounded-md" style={{
                  backgroundColor: "var(--foreground)",
                  opacity: 0.25
                }} />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  factory-01 / live
                </div>
                <MoreHorizontal className="size-4 text-muted-foreground" />
              </div>

              {}
              <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Active cycle</div>
                  <div className="font-default font-black text-base mt-1 text-foreground">CNC / Milling</div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] px-2 py-1" style={{
                border: "1.5px solid var(--foreground)",
                color: "var(--foreground)"
              }}>
                  25 ACTIVE
                </div>
              </div>

              {}
              <div className="grid grid-cols-3 gap-0" style={{
              borderTop: "2px solid var(--foreground)"
            }}>
                {columns.map((col, ci) => <div key={col.title} data-index={ci} className="p-3" style={{
                borderRight: ci < columns.length - 1 ? "2px solid var(--foreground)" : "none",
                minHeight: "320px"
              }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-default font-bold text-[11px] uppercase tracking-[0.12em] text-foreground">
                        {col.title}
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground">{col.count}</div>
                    </div>

                    <div className="flex flex-col gap-2.5">
                      {col.tasks.map((task, ti) => <div key={task.id} data-index={ti} className="p-2.5" style={{
                    border: "1.5px solid var(--foreground)",
                    backgroundColor: "var(--background)",
                    boxShadow: task.state === "active" ? "3px 3px 0 0 var(--primary)" : "3px 3px 0 0 var(--foreground)"
                  }}>
                          <div className="flex items-start gap-1.5">
                            {task.state === "done" ? <CheckCircle2 className="size-3 mt-0.5 shrink-0" style={{
                        color: "var(--primary)"
                      }} /> : task.state === "active" ? <Clock className="size-3 mt-0.5 shrink-0" style={{
                        color: "var(--primary)"
                      }} /> : <Circle className="size-3 mt-0.5 shrink-0 text-muted-foreground" />}
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
                                {task.id}
                              </div>
                              <div className="font-default font-semibold text-[11px] leading-tight mt-1 text-foreground">
                                {task.title}
                              </div>
                              <div className="font-mono text-[9px] uppercase tracking-[0.1em] mt-2 inline-block px-1.5 py-0.5" style={{
                          border: "1px solid var(--foreground)",
                          color: "var(--foreground)"
                        }}>
                                {task.tag}
                              </div>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>)}
              </div>

              {}
              <div className="grid grid-cols-3 gap-0" style={{
              borderTop: "2px solid var(--foreground)"
            }}>
                <div className="p-3" style={{
                borderRight: "2px solid var(--foreground)"
              }} data-index={0}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">OEE</div>
                  <div className="font-default font-black text-base mt-1 text-foreground">88%</div>
                </div>
                <div className="p-3" style={{
                borderRight: "2px solid var(--foreground)"
              }} data-index={1}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Status</div>
                  <div className="font-default font-black text-base mt-1 text-foreground">Optimal</div>
                </div>
                <div className="p-3" data-index={2}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Next Maint.</div>
                  <div className="font-default font-black text-base mt-1" style={{
                  color: "var(--primary)"
                }}>
                    Oct 24
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}